// frontend/src/components/stats/StatsEditor.jsx
//
// 2026-10-01 — Game Day Stats: the event-by-event editor for one game.
// Every goal, assist, shot, penalty… the voice engine heard or the keeper
// tapped, oldest first by tape time, each one editable in place (player,
// stat, period, minutes) and discardable, plus a row to add what was
// missed. Beside it, the transcript: what was actually said, with tape
// times, so a wrong call can be checked against the words before it is
// changed. Click an event → the transcript scrolls to that moment; click a
// transcript line → the "add" row takes that time.
//
// Edits go straight to PATCH /game-events/:gameId/:eventId and are the
// keeper's final word: the official pass never overwrites a row that was
// edited here, and never brings back one that was discarded (the server
// tombstones engine events instead of deleting them).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, FileText, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';

const mmss = (ms) => {
  if (ms == null || !Number.isFinite(Number(ms))) return '';
  const s = Math.max(0, Math.round(Number(ms) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const parseMmss = (txt) => {
  const m = String(txt || '').trim().match(/^(\d{1,3}):([0-5]?\d)$/);
  if (!m) return null;
  return (Number(m[1]) * 60 + Number(m[2])) * 1000;
};

const sel = { padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-1)', color: 'var(--text)', fontSize: 12, maxWidth: '100%' };

export default function StatsEditor({ teamId, gameId, data, onChange }) {
  const roster = data?.roster || [];
  const catalogue = data?.catalogue || [];
  const events = useMemo(() => (data?.events || []).slice().sort((a, b) => {
    const ta = a.audio_offset_ms ?? Number.MAX_SAFE_INTEGER, tb = b.audio_offset_ms ?? Number.MAX_SAFE_INTEGER;
    return ta - tb || String(a.created_at).localeCompare(String(b.created_at));
  }), [data]);
  const playerName = (uid) => { const p = roster.find(r => r.userId === uid); return p ? `${p.jerseyNumber != null ? '#' + p.jerseyNumber + ' ' : ''}${p.name || ''}`.trim() : ''; };
  const statLabel = (code) => catalogue.find(c => c.code === code)?.label || code;
  const skaterStats = catalogue.filter(c => c.scope !== 'team');

  const [busy, setBusy] = useState({});
  const patch = async (ev, body) => {
    setBusy(b => ({ ...b, [ev.id]: true }));
    try { await api.patch(`/teams/${teamId}/game-events/${gameId}/${ev.id}`, body); await onChange?.(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not save that change'); }
    finally { setBusy(b => ({ ...b, [ev.id]: false })); }
  };
  const discard = async (ev) => {
    if (!window.confirm(`Discard this ${statLabel(ev.event_type).toLowerCase()}${ev.user_id ? ' for ' + playerName(ev.user_id) : ''}?`)) return;
    setBusy(b => ({ ...b, [ev.id]: true }));
    try { await api.delete(`/teams/${teamId}/game-events/${gameId}/${ev.id}`); toast.success('Discarded'); await onChange?.(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not discard'); }
    finally { setBusy(b => ({ ...b, [ev.id]: false })); }
  };

  // ── add a missed event
  const [add, setAdd] = useState({ event_type: 'goal', user_id: '', period: '', at: '', value: 1 });
  const [adding, setAdding] = useState(false);
  const submitAdd = async () => {
    const meta = catalogue.find(c => c.code === add.event_type);
    if (!meta) return;
    if (meta.scope !== 'team' && !add.user_id) { toast.error('Pick the player'); return; }
    const atMs = add.at ? parseMmss(add.at) : null;
    if (add.at && atMs == null) { toast.error('Time looks like 12:34'); return; }
    setAdding(true);
    try {
      await api.post(`/teams/${teamId}/game-events/${gameId}`, { events: [{
        event_type: add.event_type, user_id: add.user_id || null, team_side: 'home', value: Number(add.value) || 1, source: 'manual',
        period: add.period ? Number(add.period) : null, audio_offset_ms: atMs,
      }] });
      setAdd(a => ({ ...a, user_id: '', at: '' }));
      await onChange?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not add that'); }
    finally { setAdding(false); }
  };

  // ── transcript: the latest recording of this game that reached the engine
  const [transcript, setTranscript] = useState(null);   // { segments, status } | null
  const [trState, setTrState] = useState('loading');
  const segRefs = useRef({});
  const [focusT, setFocusT] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: r } = await api.get(`/teams/${teamId}/recordings/${gameId}`);
        const rec = (r.recordings || []).find(x => x.engine_game_id) || null;
        if (!rec) { if (!cancelled) { setTranscript(null); setTrState('none'); } return; }
        const { data: t } = await api.get(`/teams/${teamId}/recordings/${rec.id}/transcript`);
        if (!cancelled) { setTranscript(t); setTrState(t.segments?.length ? 'ok' : 'empty'); }
      } catch { if (!cancelled) setTrState('error'); }
    })();
    return () => { cancelled = true; };
  }, [teamId, gameId]);
  const segments = transcript?.segments || [];
  const jumpTo = (ms) => {
    if (ms == null || !segments.length) return;
    const sec = ms / 1000;
    let best = 0;
    for (let i = 0; i < segments.length; i++) if (segments[i].t <= sec) best = i;
    setFocusT(segments[best].t);
    segRefs.current[best]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  return (
    <div className="px-3 py-3 gd-stats-editor" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 12 }}>
      <style>{`@media (max-width: 760px) { .gd-stats-editor { grid-template-columns: 1fr !important; } }`}</style>
      {/* ── events */}
      <div className="gd-stats-editor-left" style={{ minWidth: 0 }}>
        <div className="text-xs font-bold mb-2" style={{ color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Events ({events.length})
        </div>
        {events.length === 0 && <div className="text-xs text-secondary mb-2">Nothing recorded for this game yet. Add what happened below.</div>}
        <div className="space-y-1">
          {events.map(ev => {
            const meta = catalogue.find(c => c.code === ev.event_type);
            const voice = /^voice/.test(ev.source || '');
            return (
              <div key={ev.id} onClick={() => jumpTo(ev.audio_offset_ms)}
                className="rounded-lg px-2 py-1.5"
                style={{ background: ev.needs_review ? 'rgba(255,170,0,0.08)' : 'var(--surface-1)', border: `1px solid ${ev.needs_review ? 'rgba(255,170,0,0.5)' : 'var(--border)'}`, opacity: busy[ev.id] ? 0.5 : 1, cursor: ev.audio_offset_ms != null ? 'pointer' : 'default' }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', minWidth: 38 }}>{mmss(ev.audio_offset_ms) || '—'}</span>
                  <select value={ev.period ?? ''} onClick={e => e.stopPropagation()} onChange={e => patch(ev, { period: e.target.value ? Number(e.target.value) : null })} style={{ ...sel, width: 52 }} title="Period">
                    <option value="">P?</option>{[1, 2, 3, 4, 5].map(p => <option key={p} value={p}>P{p}</option>)}
                  </select>
                  <select value={ev.event_type} onClick={e => e.stopPropagation()} onChange={e => patch(ev, { event_type: e.target.value })} style={sel} title="Stat">
                    {catalogue.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                  {meta?.scope !== 'team' && (
                    <select value={ev.user_id || ''} onClick={e => e.stopPropagation()} onChange={e => patch(ev, { user_id: e.target.value || null, resolved: !!e.target.value })} style={{ ...sel, fontWeight: ev.user_id ? 600 : 400, color: ev.user_id ? 'var(--text)' : 'var(--puck, #ff8a00)' }} title="Player">
                      <option value="">— who? —</option>
                      {roster.map(p => <option key={p.userId} value={p.userId}>{p.jerseyNumber != null ? `#${p.jerseyNumber} ` : ''}{p.name}</option>)}
                    </select>
                  )}
                  {ev.event_type === 'penalty_minutes' && (
                    <input type="number" min="0" max="60" value={ev.value ?? 2} onClick={e => e.stopPropagation()} onChange={e => patch(ev, { value: Number(e.target.value) })} style={{ ...sel, width: 56 }} title="Minutes" />
                  )}
                  <span className="text-xs ml-auto" style={{ color: voice ? 'var(--ice)' : 'var(--text-3)' }} title={ev.source}>
                    {voice ? (ev.source === 'voice_final' ? '🎙 official' : '🎙 live') : '✋ by hand'}
                  </span>
                  {ev.needs_review && <AlertCircle size={13} style={{ color: 'var(--puck, #ff8a00)' }} title={ev.review_reason || 'needs a look'} />}
                  <button type="button" onClick={(e) => { e.stopPropagation(); discard(ev); }} disabled={busy[ev.id]} title="Discard"
                    style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2 }}>
                    <Trash2 size={13} />
                  </button>
                </div>
                {ev.transcript && <div className="text-xs mt-1" style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>“{ev.transcript}”</div>}
              </div>
            );
          })}
        </div>

        {/* add */}
        <div className="rounded-lg px-2 py-2 mt-3" style={{ border: '1px dashed var(--border)' }}>
          <div className="text-xs font-bold mb-1.5" style={{ color: 'var(--text-2)' }}>Add something that was missed</div>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={add.at} onChange={e => setAdd(a => ({ ...a, at: e.target.value }))} placeholder="12:34" style={{ ...sel, width: 62, fontFamily: 'var(--font-mono)' }} title="Tape time (optional)" />
            <select value={add.period} onChange={e => setAdd(a => ({ ...a, period: e.target.value }))} style={{ ...sel, width: 52 }}>
              <option value="">P?</option>{[1, 2, 3, 4, 5].map(p => <option key={p} value={p}>P{p}</option>)}
            </select>
            <select value={add.event_type} onChange={e => setAdd(a => ({ ...a, event_type: e.target.value, value: e.target.value === 'penalty_minutes' ? 2 : 1 }))} style={sel}>
              {skaterStats.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <select value={add.user_id} onChange={e => setAdd(a => ({ ...a, user_id: e.target.value }))} style={sel}>
              <option value="">— player —</option>
              {roster.map(p => <option key={p.userId} value={p.userId}>{p.jerseyNumber != null ? `#${p.jerseyNumber} ` : ''}{p.name}</option>)}
            </select>
            {add.event_type === 'penalty_minutes' && <input type="number" min="0" max="60" value={add.value} onChange={e => setAdd(a => ({ ...a, value: e.target.value }))} style={{ ...sel, width: 56 }} />}
            <button type="button" onClick={submitAdd} disabled={adding} className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg btn-ice">
              <Plus size={12} /> Add
            </button>
          </div>
        </div>
      </div>

      {/* ── transcript */}
      <div style={{ minWidth: 0 }}>
        <div className="flex items-center gap-1 text-xs font-bold mb-2" style={{ color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          <FileText size={12} /> What was said
        </div>
        <div style={{ maxHeight: 420, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-1)' }}>
          {trState === 'loading' && <div className="text-xs text-secondary p-2">Loading…</div>}
          {trState === 'none' && <div className="text-xs text-secondary p-2">No recording for this game. Record it live, or upload the audio or video, and the words appear here.</div>}
          {trState === 'empty' && <div className="text-xs text-secondary p-2">The transcript isn't ready yet{transcript?.status ? ` (${transcript.status})` : ''}. It lands a few minutes after the recording ends.</div>}
          {trState === 'error' && <div className="text-xs text-secondary p-2">Could not load the transcript.</div>}
          {segments.map((sg, i) => (
            <div key={i} ref={el => { segRefs.current[i] = el; }} onClick={() => setAdd(a => ({ ...a, at: mmss(sg.t * 1000) }))}
              className="px-2 py-1 text-xs" title="Use this time for a new event"
              style={{ cursor: 'pointer', background: focusT === sg.t ? 'rgba(0,200,255,0.12)' : 'transparent', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-3)', marginRight: 8 }}>{mmss(sg.t * 1000)}</span>
              {sg.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
